import React from "react";
import { useTranslation } from "react-i18next";
import { Tag, Tooltip } from "@fluentui/react-components";
import SidebarSection from "../../app-sidebar/SidebarSection";
import SidebarShowMore from "../../app-sidebar/SidebarShowMore";
import { useSidebarTagFiltersViewModel } from "./SidebarTagFilters.ViewModel";
import "./SidebarTagFilters.scss";

interface SidebarTagFiltersProps {
  isCollapsed: boolean;
}

/**
 * Tag filters in the board's filter pane.
 *
 * Options come from the board's unfiltered tasks, so selecting one tag does not
 * remove every other tag from the list. Selecting several widens the board —
 * a task carrying any of them is shown.
 */
const SidebarTagFilters: React.FC<SidebarTagFiltersProps> = ({
  isCollapsed,
}) => {
  const { t } = useTranslation();
  const vm = useSidebarTagFiltersViewModel();

  if (!vm.hasTags) {
    return null;
  }

  return (
    <SidebarSection
      title={t("board.filters.tagsHeader")}
      isOpen={vm.isSectionOpen}
      onToggle={vm.toggleSection}
      isPaneCollapsed={isCollapsed}
    >
      {vm.visibleTags.map(({ tag, count, style, initials, isSelected }) => (
        // Shown expanded too: a long tag is cut to an ellipsis there, and this
        // is where it can be read in full.
        <Tooltip
          key={tag}
          content={`${tag} (${count.toString()})`}
          relationship="label"
          positioning="after"
        >
          <button
            type="button"
            className={`sidebar-nav__link sidebar-nav__link--compact sidebar-nav__link--filter${isSelected ? " sidebar-nav__link--selected" : ""}`}
            aria-pressed={isSelected}
            onClick={() => vm.toggleTag(tag)}
          >
            {isCollapsed ? (
              // Sits in the slot a connection row gives its source icon, so
              // the two sections line up down the rail.
              <span
                className={`sidebar-tag-filters__swatch${style.backgroundColor ? "" : " sidebar-tag-filters__swatch--plain"}`}
                style={style}
                aria-hidden="true"
              >
                {initials}
              </span>
            ) : (
              <span className="sidebar-tag-filters__tag-slot">
                <Tag
                  className="sidebar-tag-filters__tag"
                  style={style}
                  size="extra-small"
                  appearance="outline"
                >
                  {tag}
                </Tag>
              </span>
            )}
            <span
              className={`sidebar-nav__badge${count === 0 ? " sidebar-nav__badge--zero" : ""}`}
            >
              {count}
            </span>
          </button>
        </Tooltip>
      ))}

      <SidebarShowMore
        hiddenCount={vm.hiddenCount}
        isExpanded={vm.isExpanded}
        isCollapsed={isCollapsed}
        onToggle={vm.toggleExpanded}
      />
    </SidebarSection>
  );
};

export default SidebarTagFilters;
